/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import ResizeObserver from "resize-observer-polyfill";
import getXAndYFromEvent from "@visx/event/lib/getXAndYFromEvent";
import styles from "src/components/chart/tooltip.module.css";
import { ChartBounds } from "src/components/chart/bounds";
import {
  ChartScale,
  ChartScales,
  MIN_SCATTER_CHART_CIRCLE_RADIUS,
  scaleAccessor,
} from "src/components/chart/scales";
import { ChartSeries } from "src/components/chart/series";
import { Icon } from "src/components/icon/icon";
import { QueryResultColumn } from "src/utils/results/types";
import { QueryResultRow } from "src/utils/results/types";
import { VisualChart } from "src/gql/visuals/types";
import { accessDateOrFloat } from "src/components/chart/scales";
import { accessString } from "src/components/chart/scales";
import { bisectLeft } from "d3-array";
import { bisectRight } from "d3-array";
import { bisector } from "d3-array";
import { sumBy } from "lodash";
import {
  chartOptionBubbleChart,
  chartOptionShowTotal,
  chartOptionName,
  chartOptionOnlyScatterCharts,
  chartOptionStack,
  chartOptionVisible,
} from "src/components/chart/options";
import { formatTitle } from "src/components/chart/format";
import { formatValue } from "src/components/chart/format";
import { isBandScale } from "src/components/chart/scales";
import { isNonNullable, isNumber } from "src/utils/types/types";
import { resultFloat } from "src/utils/results/values";
import { useTooltipInPortal } from "@visx/tooltip";
import { leftAxisColumns, rightAxisColumns } from "src/components/chart/series";
import { createCircleStyleFn } from "./legend";

interface ChartTooltip {
  visual: VisualChart;
  series: ChartSeries;
  bounds: ChartBounds;
  scales: ChartScales;
}

const ChartTooltipBase: React.FC<ChartTooltip> = (props) => {
  const { TooltipInPortal } = useTooltipInPortal({ polyfill: ResizeObserver });
  const [index, setIndex] = React.useState<number>(-1);
  const row = props.series.rows[index];
  const { updatePointerEvents, chartX, chartY, pointerY, pointerX } =
    useChartTooltipPointerEvents();

  const onMouseLeave = () => {
    setIndex(-1);
  };

  const onMouseMove = (event: React.MouseEvent | React.TouchEvent) => {
    const { chartX } = updatePointerEvents(event);

    setIndex(
      getXAxisClosestRowIndex({
        xScale: props.scales.x,
        series: props.series,
        reverseX: props.visual.options.reverseX,
        chartX,
      })
    );
  };

  const children = React.useMemo(() => {
    if (row) {
      return (
        <ChartTooltipContentBase
          visual={props.visual}
          series={props.series}
          scales={props.scales}
          row={row}
        />
      );
    }
  }, [props.visual, props.series, row]);

  return (
    <>
      <rect
        width={props.bounds.xMax}
        height={props.bounds.yMax}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        fill="transparent"
      />
      {row && (
        <>
          <ChartTooltipCrosshair bounds={props.bounds} x={chartX} y={chartY} />
          <TooltipInPortal
            className={styles.tooltip}
            top={pointerY}
            left={pointerX}
          >
            {children}
          </TooltipInPortal>
        </>
      )}
    </>
  );
};

const useChartTooltipPointerEvents = () => {
  const [pointerX, setPointerX] = React.useState(0);
  const [pointerY, setPointerY] = React.useState(0);
  const [chartX, setChartX] = React.useState(0);
  const [chartY, setChartY] = React.useState(0);

  const updatePointerEvents = (event: React.MouseEvent | React.TouchEvent) => {
    const rect = (event.target as SVGRectElement).getBoundingClientRect();
    const pointer = getXAndYFromEvent(event);
    const chartX = pointer.x - rect.x;
    const chartY = pointer.y - rect.y;
    setPointerX(pointer.x);
    setPointerY(pointer.y);
    setChartX(chartX);
    setChartY(chartY);

    return { chartX, chartY };
  };

  return { updatePointerEvents, chartX, chartY, pointerX, pointerY };
};

interface GetXAxisClosestRowIndex {
  xScale: ChartScale;
  series: ChartSeries;
  chartX: number;
  reverseX?: boolean;
}

const getXAxisClosestRowIndex = ({
  xScale,
  series,
  reverseX,
  chartX,
}: GetXAxisClosestRowIndex) => {
  if (isBandScale(xScale)) {
    const xList = series.rows.map((row) => row[series.x.name]).map(String);
    const range = xList.map(xScale).map(resultFloat);

    if (reverseX) {
      const index = bisectRight(range.reverse(), chartX - xScale.step());

      return range.length - 1 - index;
    } else {
      return bisectLeft(range, chartX - xScale.step());
    }
  } else {
    const bisect = bisector(accessDateOrFloat(series.x));
    return bisect.center(series.rows, xScale.invert(chartX));
  }
};

interface ChartTooltipBubbleChart {
  groupColumnName?: string;
}

const ChartTooltipBubbleChart: React.FC<
  ChartTooltip & ChartTooltipBubbleChart
> = (props) => {
  const { TooltipInPortal } = useTooltipInPortal({ polyfill: ResizeObserver });
  const { updatePointerEvents, pointerX, pointerY, chartX, chartY } =
    useChartTooltipPointerEvents();
  const [index, setIndex] = React.useState(-1);
  const [activeColumnName, setActiveColumnName] = React.useState<string | null>(
    null
  );
  const row = props.series.rows[index];

  const onMouseMove = (event: React.MouseEvent | React.TouchEvent) => {
    const { chartX, chartY } = updatePointerEvents(event);
    const activeRowIndexes: Array<number> = [];
    const activeColumnNames: Record<number, QueryResultColumn["name"]> = {};

    props.series.rows.forEach((row, index) => {
      const xPixelPosition = scaleAccessor(props.scales.x, props.series.x)(row);
      // props.scales.z should always be defined
      const zRadius = props.scales.z
        ? scaleAccessor(props.scales.z, props.series.z)(row)
        : MIN_SCATTER_CHART_CIRCLE_RADIUS;

      const [leftBoundary, rightBoundary] = [
        xPixelPosition - zRadius,
        xPixelPosition + zRadius,
      ];

      // Find all rows in the x-axis area radius
      if (chartX >= leftBoundary && chartX <= rightBoundary) {
        const y0Scale = props.scales.y0;

        // y0Scale should always be defined
        if (y0Scale) {
          // Find all rows in the y-axis area radius
          props.series.y0.scatter.forEach((column) => {
            const yPosition = scaleAccessor(y0Scale, column)(row);

            // With grouped values, some y columns might not exist in all the rows
            if (isNonNullable(row[column.name])) {
              const [leftBoundary, rightBoundary] = [
                yPosition - zRadius,
                yPosition + zRadius,
              ];

              // Currently multiple y series will have the same radius since they share the same z column.
              // If the mouse cursor is inside both bubbles we will set the active column name to the last column we find since we only show one bubble value in the tooltip.
              if (chartY >= leftBoundary && chartY <= rightBoundary) {
                activeColumnNames[index] = column.name;
                activeRowIndexes.push(index);
              }
            }
          });
        }
      }
    });

    // Find a row with the smallest z value in the cross-hair
    let [activeIndex] = activeRowIndexes.reduce<[number, number | null]>(
      ([minIndex, minValue], rowIndex) => {
        const zValue = props.series.rows[rowIndex][props.series.z.name];

        if (isNumber(zValue) && (!minValue || zValue < minValue)) {
          return [rowIndex, zValue];
        }

        return [minIndex, minValue];
      },
      [-1, null]
    );

    // If no bubble was found, use the closest x-axis row index if any of the series are not scatter plots.
    if (activeIndex < 0 && !chartOptionOnlyScatterCharts(props.visual)) {
      activeIndex = getXAxisClosestRowIndex({
        xScale: props.scales.x,
        series: props.series,
        reverseX: props.visual.options.reverseX,
        chartX,
      });
    }

    setIndex(activeIndex);
    // Save the active column name, so we know which bubble is active
    setActiveColumnName(activeColumnNames[activeIndex] || null);
  };

  const onMouseLeave = () => {
    setIndex(-1);
    setActiveColumnName(null);
  };

  const children = React.useMemo(() => {
    if (row) {
      return (
        <ChartTooltipContentBubble
          activeColumnName={activeColumnName}
          groupColumnName={props.groupColumnName}
          visual={props.visual}
          series={props.series}
          scales={props.scales}
          row={row}
        />
      );
    }
  }, [props.visual, props.series, row, activeColumnName]);

  return (
    <>
      <rect
        width={props.bounds.xMax}
        height={props.bounds.yMax}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        fill="transparent"
      />
      {row && (
        <>
          <ChartTooltipCrosshair bounds={props.bounds} x={chartX} y={chartY} />
          <TooltipInPortal
            className={styles.tooltip}
            top={pointerY}
            left={pointerX}
          >
            {children}
          </TooltipInPortal>
        </>
      )}
    </>
  );
};

export const ChartTooltipPie = (props: {
  visual: VisualChart;
  series: ChartSeries;
  scales: ChartScales;
  row: QueryResultRow;
  children: (handlers: {
    onMouseMove(event: React.MouseEvent<SVGPathElement, MouseEvent>): void;
    onMouseLeave(): void;
  }) => React.ReactNode;
}) => {
  const { TooltipInPortal } = useTooltipInPortal({ polyfill: ResizeObserver });
  const [x, setX] = React.useState(0);
  const [y, setY] = React.useState(0);

  const onMouseLeave = () => {
    setX(0);
    setY(0);
  };

  const onMouseMove = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    setX(event.clientX);
    setY(event.clientY);
  };

  const children = React.useMemo(() => {
    return (
      <ChartTooltipContentBase
        visual={props.visual}
        series={props.series}
        scales={props.scales}
        row={props.row}
      />
    );
  }, [props.visual, props.series, props.row]);

  return (
    <>
      {props.children({
        onMouseMove,
        onMouseLeave,
      })}
      {x && y && (
        <TooltipInPortal className={styles.tooltip} top={y} left={x}>
          {children}
        </TooltipInPortal>
      )}
    </>
  );
};

const ChartTooltipContentBase: React.FC<{
  visual: VisualChart;
  series: ChartSeries;
  scales: ChartScales;
  row: QueryResultRow;
}> = (props) => {
  const createCircleStyle = createCircleStyleFn(props);

  const getColumnName = (column: QueryResultColumn) => {
    return props.visual.options.globalSeriesType === "pie"
      ? accessString(props.series.x)(props.row)
      : column.name;
  };

  // We reverse the order of the tooltip items to match the order of the stacked series in the chart
  const leftYs = leftAxisColumns(props.series)
    .filter((y) => {
      return isNonNullable(props.row[y.name]);
    })
    .reverse();
  const rightYs = rightAxisColumns(props.series)
    .filter((y) => {
      return isNonNullable(props.row[y.name]);
    })
    .reverse();

  // We compute the total of the y series when the chart is stacked to display it in the tooltip
  const sumFn = (columns: QueryResultColumn[]) =>
    sumBy(columns, function (column) {
      return Number(props.row[column.name] || 0);
    });

  const rightTotal =
    chartOptionStack(props.visual) && chartOptionShowTotal(props.visual)
      ? sumFn(rightYs)
      : null;
  const leftTotal =
    chartOptionStack(props.visual) && chartOptionShowTotal(props.visual)
      ? sumFn(leftYs)
      : null;

  return (
    <>
      <h1>{formatTitle(props.series.x, props.row[props.series.x.name])}</h1>
      <ul>
        {leftYs.map((column) => {
          const isVisible = chartOptionVisible(props.visual, column.name);
          return (
            isVisible && (
              <li key={column.name}>
                <span style={createCircleStyle(getColumnName(column))}>
                  <Icon icon="circle-fill" />
                </span>
                <span>{chartOptionName(props.visual, column.name)}</span>
                <span>
                  {formatValue(
                    props.visual,
                    props.series,
                    column,
                    props.row[column.name]
                  )}
                </span>
              </li>
            )
          );
        })}

        {
          // Display the total of the y series when the chart is stacked and it has items
          Boolean(leftTotal && leftYs.length > 1) && (
            <li className={styles.noColor}>
              <span>Total</span>
              <span>
                {formatValue(
                  props.visual,
                  props.series,
                  props.series.y0.column[0],
                  leftTotal
                )}
              </span>
            </li>
          )
        }

        {rightYs.map((column) => {
          const isVisible = chartOptionVisible(props.visual, column.name);
          return (
            isVisible && (
              <li key={column.name}>
                <span style={createCircleStyle(getColumnName(column))}>
                  <Icon icon="circle-fill" />
                </span>
                <span>{chartOptionName(props.visual, column.name)}</span>
                <span>
                  {formatValue(
                    props.visual,
                    props.series,
                    column,
                    props.row[column.name],
                    {
                      isRightYAxisSeries: true,
                    }
                  )}
                </span>
              </li>
            )
          );
        })}

        {
          // Display the total of the y series when the chart is stacked and it has items
          Boolean(rightTotal && rightYs.length > 1) && (
            <li className={styles.noColor}>
              <h1>Total</h1>
              <span>
                {formatValue(
                  props.visual,
                  props.series,
                  props.series.y1.column[0],
                  rightTotal
                )}
              </span>
            </li>
          )
        }
      </ul>
    </>
  );
};

export const filterBubbleChartColumns = (
  series: ChartSeries,
  row: QueryResultRow,
  activeColumnName: string | null
) => {
  const scatterChartColumnNames = series.y0.scatter.map(({ name }) => name);

  return leftAxisColumns(series).filter((column) => {
    const isScatterChartType = scatterChartColumnNames.includes(column.name);
    const isActiveScatterChartType = column.name === activeColumnName;

    return (
      isNonNullable(row[column.name]) &&
      (!activeColumnName || !isScatterChartType || isActiveScatterChartType)
    );
  });
};

const ChartTooltipContentBubble: React.FC<
  {
    visual: VisualChart;
    series: ChartSeries;
    scales: ChartScales;
    row: QueryResultRow;
    activeColumnName: string | null;
  } & ChartTooltipBubbleChart
> = ({ groupColumnName, activeColumnName, series, row, visual, scales }) => {
  const yColumns = filterBubbleChartColumns(series, row, activeColumnName);
  const createCircleStyle = createCircleStyleFn({ series, scales, visual });

  const title = groupColumnName
    ? // Grouped y columns have only one y value
      yColumns[0].name
    : formatTitle(series.x, row[series.x.name]);

  return (
    <>
      <div className={styles.title}>
        {groupColumnName && (
          <span style={createCircleStyle(yColumns[0].name)}>
            <Icon icon="circle-fill" />
          </span>
        )}
        <h1>{title}</h1>
      </div>
      <ul>
        {!groupColumnName &&
          yColumns.map((column) => {
            const isVisible = chartOptionVisible(visual, column.name);
            return (
              isVisible && (
                <li key={column.name}>
                  <span style={createCircleStyle(column.name)}>
                    <Icon icon="circle-fill" />
                  </span>
                  <span>{chartOptionName(visual, column.name)}</span>
                  <span>
                    {formatValue(visual, series, column, row[column.name])}
                  </span>
                </li>
              )
            );
          })}
        {groupColumnName && (
          <>
            <li className={styles.noColor}>
              <span>{series.x.name}</span>
              <span>{formatTitle(series.x, row[series.x.name])}</span>
            </li>
            <li className={styles.noColor}>
              <span>{groupColumnName}</span>
              <span>
                {formatValue(
                  visual,
                  series,
                  yColumns[0],
                  row[yColumns[0].name]
                )}
              </span>
            </li>
          </>
        )}
        {scales.z && (
          <li className={styles.noColor}>
            <span>{chartOptionName(visual, series.z.name)}</span>
            <span>
              {formatValue(visual, series, series.z, row[series.z.name])}
            </span>
          </li>
        )}
      </ul>
    </>
  );
};

const ChartTooltipCrosshair: React.FC<{
  bounds: ChartBounds;
  x: number;
  y: number;
}> = (props) => {
  return (
    <g className={styles.crosshair}>
      <line x1={props.x} x2={props.x} y1={0} y2={props.bounds.yMax} />
      <line x1={0} x2={props.bounds.xMax} y1={props.y} y2={props.y} />
    </g>
  );
};

export const ChartTooltip: React.FC<{
  visual: VisualChart;
  series: ChartSeries;
  bounds: ChartBounds;
  scales: ChartScales;
  groupColumnName?: string;
}> = ({ groupColumnName, ...props }) => {
  if (chartOptionBubbleChart(props.visual, props.scales.z)) {
    return (
      <ChartTooltipBubbleChart groupColumnName={groupColumnName} {...props} />
    );
  }

  return <ChartTooltipBase {...props} />;
};
